// companies-marquee.js — mobile-only marquee with drag-to-modulate-speed
// Always auto-scrolls. Drag temporarily nudges the speed based on finger
// velocity (faster, slower, or reversed), then eases back to the auto base.
//
// Implementation note: instead of cloning the whole list and using a wrap
// modifier (which is sensitive to any margin/gap between siblings and tends
// to glitch at iteration boundaries), we duplicate the list's children
// inline and animate xPercent from 0 to -50. At -50% the second half lines
// up exactly where the first half was, so the loop is seamless by
// construction — no per-pixel wrap math required.
//
// Requires: gsap, Observer (loaded globally on the Webflow page).

export function initCompaniesMarquee() {
  const DURATION = 18; // seconds per full marquee cycle (auto speed)
  const SENSITIVITY = 0.03; // finger velocity (px/s) → timeScale offset
  const MAX_TIMESCALE = 25; // cap so very fast flicks don't go absurd
  const BOOST_DURATION = 0.12; // how fast the boost applies after a drag tick
  const DECAY_DURATION = 0.5; // how fast it eases back to auto base after release

  const rows = [
    { wrapperSel: '.infinite-top', listSel: '.companies_logos-top', direction: 'left' },
    { wrapperSel: '.infinite-bottom', listSel: '.companies_logos-bottom', direction: 'right' },
  ];

  function destroy() {
    rows.forEach(({ wrapperSel }) => {
      const wrapper = document.querySelector(wrapperSel);
      if (!wrapper) return;

      const list = wrapper.querySelector('[data-marquee-list]');
      if (list) {
        gsap.killTweensOf(list);
        // Remove the inline clones we added on init.
        list.querySelectorAll('[data-marquee-clone]').forEach((el) => el.remove());
        list.removeAttribute('data-marquee-list');
        gsap.set(list, { clearProps: 'all' });
      }

      delete wrapper.dataset.marqueeInit;
    });
  }

  function init() {
    if (window.innerWidth >= 992) return;

    rows.forEach(({ wrapperSel, listSel, direction }) => {
      const wrapper = document.querySelector(wrapperSel);
      if (!wrapper || wrapper.dataset.marqueeInit) return;

      const list = wrapper.querySelector(listSel);
      if (!list) return;

      list.setAttribute('data-marquee-list', '');
      list.style.cssText +=
        ';display:flex;flex-wrap:nowrap;flex-shrink:0;width:max-content;will-change:transform;';

      // Duplicate every child once, in place. The list now contains 2x the
      // original content. Animating from 0 to -50% lines the second half up
      // with the original first-half position → perfectly seamless loop.
      const originals = Array.from(list.children);
      originals.forEach((child) => {
        const clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-marquee-clone', '');
        list.appendChild(clone);
      });

      const baseDirection = direction === 'right' ? -1 : 1;

      const loop = gsap.to(list, {
        xPercent: -50,
        duration: DURATION,
        ease: 'none',
        repeat: -1,
        // Reverse-direction safety: keep the tween primed at progress 1 so it
        // doesn't get stuck at progress 0 when timeScale is negative.
        onReverseComplete: () => loop.progress(1),
      });
      if (baseDirection < 0) loop.progress(1);
      loop.timeScale(baseDirection);

      // Mutable container so we can tween its `value` and apply it each frame
      // to the loop's timeScale.
      const timeScale = { value: baseDirection };
      const applyScale = () => loop.timeScale(timeScale.value);

      Observer.create({
        target: wrapper,
        type: 'touch,pointer',
        preventDefault: false,
        lockAxis: true,
        tolerance: 5,
        dragMinimum: 5,

        // Each drag-tick: target timeScale is finger velocity × sensitivity
        // (sign inverted: drag right → marquee right). Tween toward that
        // target over BOOST_DURATION, then ease back to the auto base over
        // DECAY_DURATION. Re-fires on every drag movement, so the marquee
        // tracks finger speed continuously while held.
        onChangeX: (self) => {
          const target = gsap.utils.clamp(
            -MAX_TIMESCALE,
            MAX_TIMESCALE,
            self.velocityX * -SENSITIVITY
          );

          gsap.killTweensOf(timeScale);
          gsap
            .timeline({ onUpdate: applyScale })
            .to(timeScale, { value: target, duration: BOOST_DURATION, overwrite: true })
            .to(timeScale, { value: baseDirection, duration: DECAY_DURATION });
        },
      });

      wrapper.dataset.marqueeInit = 'true';
    });
  }

  init();

  let lastMobile = window.innerWidth < 992;
  let resizeTimer;

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const isMobile = window.innerWidth < 992;
      if (isMobile === lastMobile) return;
      lastMobile = isMobile;
      destroy();
      init();
    }, 200);
  });
}
